import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft,
  Shield,
  Lock,
  Database,
  Users,
  AlertTriangle,
  CheckCircle,
  FileText,
  Eye,
  Settings
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

const SeguridadPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white/60">
      <PageHeader hideCTA={true} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="text-red-600" size={32} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('seguridad.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('seguridad.subtitle')}
            </p>
          </div>

          {/* Información del Sistema */}
          <div className="bg-gray-50 rounded-lg p-6 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-red-600" size={24} />
              <h2 className="text-xl font-semibold text-[#141414]">{t('seguridad.statement_title')}</h2>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <span className="font-medium">Fecha de Revisión:</span> 02/01/2023 | 
              <span className="font-medium ml-2">Fecha de Aprobación:</span> 02/01/2023
            </div>
          </div>

          {/* Descripción Principal */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {t('seguridad.main_desc')}
            </p>
          </div>

          {/* Políticas de Seguridad */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#141414] mb-8 text-center">{t('seguridad.policies_title')}</h2>
            
            <div className="grid gap-6">
              
              {/* Compromiso con la Seguridad */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.commitment.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.commitment.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Protección de Activos */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Database className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.assets.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.assets.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Control de Acceso */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Lock className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.access.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.access.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Gestión de Incidentes */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.incidents.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.incidents.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Capacitación y Concienciación */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.training.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.training.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cumplimiento Legal */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="text-indigo-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.legal.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.legal.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mejora Continua */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Settings className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.improvement.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.improvement.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Monitoreo y Auditoría */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Eye className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('seguridad.items.audit.title')}</h3>
                    <p className="text-gray-600">
                      {t('seguridad.items.audit.desc')}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Compromiso Final */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <Shield className="text-red-600" size={32} />
            </div>
            <p className="text-red-800 font-medium mb-2">
              {t('seguridad.commitment_final')}
            </p>
            <p className="text-red-700 text-sm">
              {t('seguridad.commitment_desc')}
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SeguridadPage;
